import {
  BufferGeometry, type Color, Float32BufferAttribute, type Object3D, Points, PointsMaterial, type Vector3,
} from 'three';
import { Pcg32 } from '../../sim/rng';
import { patchMrtUnlit } from '../materials/toon';
import { RAIN, RainStreaks } from './rain';

export type WeatherKind = 'clear' | 'snow' | 'blizzard' | 'rain' | 'sandstorm' | 'thunderstorm';

interface WeatherSpec {
  count: number;
  fall: number; // m/s downward
  wind: [number, number]; // m/s along x, z
  flutter: number; // sideways wobble, metres
  /** Gusting: the wind swells and slackens by this fraction of itself (0 = steady). */
  gust: number;
}

const SPECS: Record<Exclude<WeatherKind, 'clear' | 'rain' | 'thunderstorm'>, WeatherSpec> = {
  snow: { count: 420, fall: 1.7, wind: [0.9, 0.3], flutter: 0.5, gust: 0 },
  blizzard: { count: 1500, fall: 2.6, wind: [6.5, 1.2], flutter: 0.8, gust: 0.7 },
  sandstorm: { count: 1300, fall: 0.5, wind: [9.0, 1.5], flutter: 0.6, gust: 0.35 },
};

/** Two slow swells that never quite repeat: a long one for the squall, a short one on top of it. */
const GUSTS: ReadonlyArray<readonly [share: number, rate: number, phase: number]> = [[0.7, 0.45, 0], [0.3, 1.3, 1]];

/**
 * Seconds of steady wind that a gusting wind has blown by `time`: the integral of
 * `1 + gust * sum(share * sin(rate t + phase))`, in closed form. Particles are still placed by
 * a pure function of time, so gusts cost the scroll-stability property nothing.
 */
export function gustTravel(time: number, gust: number): number {
  let travel = time;
  for (const [share, rate, phase] of GUSTS) {
    travel -= ((gust * share) / rate) * (Math.cos(rate * time + phase) - Math.cos(phase));
  }
  return travel;
}

/** How hard it is blowing right now, as a multiple of the steady wind (for whoever wants to lean the trees). */
export function gustStrength(time: number, gust: number): number {
  let k = 1;
  for (const [share, rate, phase] of GUSTS) k += gust * share * Math.sin(rate * time + phase);
  return k;
}

export interface WeatherOptions {
  /** Where rain lands. Without it drops land on y = 0, which is right for flat ground only. */
  groundY?: (x: number, z: number) => number;
}

/**
 * Camera-following weather (05 §9): single-texel points for snow, blizzard and sand, streaks
 * for rain and for the thunderstorm's heavier rain (rain.ts). Everything is placed in WORLD space
 * by a pure function of time and only
 * wrapped around the view, so a few hundred particles cover any map and none of them moves
 * because the camera did.
 */
export class Weather {
  /** Everything this weather draws, or null when clear: add it to the scene. (Named for what it used to be.) */
  readonly points: Object3D | null;
  private readonly flakes: Points | null = null;
  private readonly rain: RainStreaks | null = null;
  private readonly spec: WeatherSpec | null = null;
  private readonly base: Float32Array = new Float32Array(0);
  private readonly phase: Float32Array = new Float32Array(0);
  private readonly size = { x: 52, y: 16, z: 40 };

  constructor(kind: WeatherKind, color: Color, options: WeatherOptions = {}) {
    if (kind === 'clear') {
      this.points = null;
      return;
    }
    if (kind === 'rain' || kind === 'thunderstorm') {
      // ROUND5: a thunderstorm is heavy rain and nothing else on screen - the stroke that lights the
      // map (WorldLights.setFlash) and the clap behind it (audio/thunder.ts) are what make it a
      // thunderstorm. A particle of its own would only make a temperate downpour a different substance.
      const perCell = kind === 'thunderstorm' ? RAIN.heavyPerCell : RAIN.perCell;
      this.rain = new RainStreaks(color, options.groundY ?? null, perCell);
      this.points = this.rain.lines;
      return;
    }
    const spec = SPECS[kind];
    this.spec = spec;
    const rng = new Pcg32(7, 77);
    this.base = new Float32Array(spec.count * 3);
    this.phase = new Float32Array(spec.count);
    for (let i = 0; i < spec.count; i++) {
      this.base[i * 3] = rng.float() * this.size.x;
      this.base[i * 3 + 1] = rng.float() * this.size.y;
      this.base[i * 3 + 2] = rng.float() * this.size.z;
      this.phase[i] = rng.float() * Math.PI * 2;
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(new Float32Array(spec.count * 3), 3));
    const material = new PointsMaterial({ color, size: 1, sizeAttenuation: false });
    material.onBeforeCompile = patchMrtUnlit;
    material.customProgramCacheKey = () => 'points-mrt';
    this.flakes = new Points(geometry, material);
    this.flakes.frustumCulled = false;
    this.points = this.flakes;
  }

  /**
   * `zoomOut` is how far the camera has pulled back from the ground view (1 on the ground, about 3
   * over the map in the aircraft). The weather's box is sized for the ground view, so from the air it
   * was a patch of rain in the middle of the screen: it is scaled about the focus by the same factor,
   * which keeps it edge to edge with the same density and streak length ON SCREEN at every zoom.
   */
  update(time: number, focus: Vector3, zoomOut = 1): void {
    if (this.points) {
      const k = Math.max(1, zoomOut);
      this.points.scale.setScalar(k);
      // Scaling about the focus, not the origin: the box must stay centred on what the camera sees.
      this.points.position.set(focus.x * (1 - k), 0, focus.z * (1 - k));
    }
    if (this.rain) this.rain.update(time, focus.x, focus.z);
    if (!this.flakes || !this.spec) return;
    const { fall, wind, flutter, gust } = this.spec;
    const attr = this.flakes.geometry.getAttribute('position');
    const { x: sx, y: sy, z: sz } = this.size;
    const wrap = (v: number, m: number): number => ((v % m) + m) % m;
    // In a gust the snow runs flatter and wilder: more wind travelled, more wobble.
    const blown = gust > 0 ? gustTravel(time, gust) : time;
    const wobble = flutter * (gust > 0 ? 0.6 + 0.4 * gustStrength(time, gust) : 1);
    for (let i = 0; i < this.phase.length; i++) {
      const ph = this.phase[i] ?? 0;
      const bx = this.base[i * 3] ?? 0;
      const by = this.base[i * 3 + 1] ?? 0;
      const bz = this.base[i * 3 + 2] ?? 0;
      const x = wrap(bx + wind[0] * blown + Math.sin(time * 1.3 + ph) * wobble - focus.x, sx) - sx / 2 + focus.x;
      const y = wrap(by - fall * time, sy);
      const z = wrap(bz + wind[1] * blown + Math.cos(time * 1.1 + ph) * wobble - focus.z, sz) - sz / 2 + focus.z;
      attr.setXYZ(i, x, y, z);
    }
    attr.needsUpdate = true;
  }
}
